/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable no-console */
/* eslint-disable react/function-component-definition */
/* eslint-disable react/no-array-index-key */
import {
  Fragment,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Send,
  Loader2,
  Sparkles,
  Wand2,
  Plus,
  Zap,
  LineChart as LineIcon,
  Layers,
  ThermometerSun,
  Wind,
  Newspaper,
  Settings2,
  MessagesSquare,
  Paperclip,
  FileSpreadsheet,
  X as CloseIcon
} from 'lucide-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useChat } from '@ai-sdk/react';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  DefaultChatTransport,
  getToolName,
  isToolUIPart,
  lastAssistantMessageIsCompleteWithToolCalls,
  type UIMessage
} from 'ai';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useSearchParams } from 'react-router-dom';
import MarkdownText from '../components/Data/Markdown';
import { useData } from '../context/data.context';
import usePlan from '../hooks/usePlan';
import { RenderBlock, UploadedDataset, getAiChatUrl } from '../services/ai-agent.service';
import { buildAgentTools } from '../services/ai-agent-tools';
import { takeChartAiHandoff } from '../services/chart-ai-handoff';
import AgentBlock, {
  ChartSettings,
  ChartSettingsProvider,
  DEFAULT_CHART_SETTINGS,
  clampChartSettings
} from '../components/AiAgent/AgentBlocks';
import ChartSidebar from '../components/AiAgent/ChatSidebar';
import {
  ChatSummary,
  deleteChat,
  deriveChatTitle,
  listChats,
  loadChat,
  newChatId,
  saveChat
} from '../services/chat-storage';
import {
  getTokenFromSessionStorage,
  getUserNameFromSessionStorage
} from '../services/session-storage-service';

/**
 * The "open the page → get insights immediately" briefing prompt. Triggers
 * Proactive Briefing Mode in the system prompt — snapshot KPIs, human-impact
 * card, action recommendations, plus a 2-3 sentence narrative.
 */
const BRIEFING_PROMPT =
  'Brief me on what is happening across all sensors right now. Pull the current snapshot, summarise the last 24 hours for CO2, PM2.5 and TVOC, then translate the numbers into human impact, and finish with 2–3 concrete next steps. Tight prose, no filler.';

/** Maximum number of rows we keep from an uploaded file (guard memory). */
const UPLOAD_MAX_ROWS = 5000;

/**
 * Parse a CSV or Excel file dropped/picked by the user. Returns null if the
 * extension is not recognised or the file is empty.
 * @param file - The browser File handle.
 * @returns A normalised dataset, or null.
 */
async function parseUploadedFile(file: File): Promise<UploadedDataset | null> {
  const filename = file.name;
  const isExcel = /\.(xlsx|xls)$/i.test(filename);
  const isCsv = /\.(csv|tsv|txt)$/i.test(filename);

  // Excel via read-excel-file (already in deps).
  if (isExcel) {
    const readXlsxFile = (await import('read-excel-file')).default;
    const raw = await readXlsxFile(file);
    if (!raw.length) return null;
    const headers = raw[0].map((c) => (c == null ? '' : String(c)));
    const rows = raw.slice(1, 1 + UPLOAD_MAX_ROWS).map((r) =>
      r.map((v) => {
        if (v === null || v === undefined) return null;
        if (typeof v === 'number') return v;
        if (v instanceof Date) return v.toISOString();
        return String(v);
      })
    );
    return { filename, uploadedAt: Date.now(), headers, rows };
  }

  if (isCsv) {
    const text = await file.text();
    const delim = filename.toLowerCase().endsWith('.tsv') ? '\t' : ',';
    const lines = text.split(/\r?\n/).filter((l) => l.length);
    if (!lines.length) return null;
    const parseLine = (line: string): string[] => {
      const out: string[] = [];
      let cur = '';
      let inQuote = false;
      for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (inQuote) {
          if (ch === '"' && line[i + 1] === '"') {
            cur += '"';
            i += 1;
          } else if (ch === '"') {
            inQuote = false;
          } else {
            cur += ch;
          }
        } else if (ch === '"') {
          inQuote = true;
        } else if (ch === delim) {
          out.push(cur);
          cur = '';
        } else {
          cur += ch;
        }
      }
      out.push(cur);
      return out;
    };
    const headers = parseLine(lines[0]).map((s) => s.trim());
    const rows = lines.slice(1, 1 + UPLOAD_MAX_ROWS).map((line) =>
      parseLine(line).map((v) => {
        const trimmed = v.trim();
        if (trimmed === '') return null;
        const num = Number(trimmed);
        return Number.isFinite(num) && /^-?\d+(\.\d+)?$/.test(trimmed) ? num : trimmed;
      })
    );
    return { filename, uploadedAt: Date.now(), headers, rows };
  }

  return null;
}

const SUGGESTIONS: { icon: ReactElement; label: string; prompt: string }[] = [
  {
    icon: <Newspaper className="h-4 w-4" />,
    label: 'Brief me on what matters now',
    prompt: BRIEFING_PROMPT
  },
  {
    icon: <ThermometerSun className="h-4 w-4" />,
    label: 'What happened in the last 24h?',
    prompt:
      'Give me a concise narrative of what happened across all sensors in the last 24 hours. Highlight any meaningful changes, peaks or quiet periods, and back it with metric cards.'
  },
  {
    icon: <LineIcon className="h-4 w-4" />,
    label: 'Show CO₂ trend (last 24h)',
    prompt:
      'Show me a line chart of CO₂ over the last 24 hours, then explain the pattern in 2–3 sentences.'
  },
  {
    icon: <Wind className="h-4 w-4" />,
    label: 'Compare PM2.5 across devices',
    prompt:
      'Which devices have the highest average PM2.5 over the last 24 hours? Render a bar chart and call out the top offender.'
  },
  {
    icon: <LineIcon className="h-4 w-4" />,
    label: 'CO₂ vs PM2.5 (one chart)',
    prompt:
      'Plot CO₂ and PM2.5 on the SAME chart for the last 24 hours across all devices. Then explain whether they move together or independently in 2 sentences.'
  },
  {
    icon: <Layers className="h-4 w-4" />,
    label: 'Quick situational snapshot',
    prompt:
      'Give me a quick situational snapshot right now — current readings as KPI cards plus a one-line interpretation.'
  }
];

/** Render-block payloads keyed by the tool call that produced them. */
type DisplayMap = Record<string, RenderBlock[]>;

function phaseLabelForTool(name?: string): string {
  switch (name) {
    case 'list_capabilities':
      return 'Looking up sensors and parameters…';
    case 'get_current_snapshot':
      return 'Reading the live snapshot…';
    case 'summarize_period':
      return 'Crunching stats…';
    case 'render_line_chart':
      return 'Pulling time-series & drawing chart…';
    case 'render_metric_cards':
      return 'Building KPI cards…';
    case 'render_callout':
      return 'Highlighting key finding…';
    case 'compare_devices':
      return 'Comparing devices…';
    default:
      return 'Working on it…';
  }
}

// Concatenate the text parts of a message into a single string.
function messageText(message: UIMessage): string {
  return (message.parts || [])
    .filter((p) => p.type === 'text')
    .map((p) => (p as { text?: string }).text || '')
    .join('');
}

// True while a tool call has been emitted but its result is not back yet —
// i.e. a client-side tool executor is still running. During this window
// useChat's `status` is 'ready' (no HTTP request in flight), so it must be
// tracked separately or the loader would disappear mid-run.
function hasPendingToolCall(messages: UIMessage[]): boolean {
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'assistant') return false;
  return (last.parts || []).some(
    (p) =>
      isToolUIPart(p) &&
      (p.state === 'input-available' || p.state === 'input-streaming')
  );
}

// The agent is "working" while a request is streaming OR a client-side tool
// is executing between requests.
function isAgentWorking(messages: UIMessage[], status: string): boolean {
  return (
    status === 'submitted' ||
    status === 'streaming' ||
    hasPendingToolCall(messages)
  );
}

// Derive the "what is the agent doing" pill from chat status and the
// in-flight assistant message. Returns null when nothing should be shown.
function derivePhase(
  messages: UIMessage[],
  status: string
): string | null {
  if (!isAgentWorking(messages, status)) return null;
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'assistant') return 'Thinking…';

  const runningTool = [...(last.parts || [])]
    .reverse()
    .find(
      (p) =>
        isToolUIPart(p) &&
        (p.state === 'input-available' || p.state === 'input-streaming')
    );
  if (runningTool) return phaseLabelForTool(getToolName(runningTool));

  const hasText = (last.parts || []).some(
    (p) => p.type === 'text' && (p as { text?: string }).text
  );
  // Text is already streaming in — no need for a separate pill.
  return hasText ? null : 'Thinking…';
}

function BackgroundOrnament(): ReactElement {
  return (
    <>
      <div
        className="pointer-events-none absolute -top-20 left-1/2 z-0 h-80 w-[80%] -translate-x-1/2 rounded-full opacity-30 blur-3xl"
        style={{
          background:
            'radial-gradient(closest-side, #6366f1 0%, #a855f7 40%, transparent 70%)'
        }}
      />
      <div
        className="pointer-events-none absolute bottom-0 right-0 z-0 h-72 w-72 rounded-full opacity-20 blur-3xl"
        style={{
          background: 'radial-gradient(closest-side, #06b6d4 0%, transparent 70%)'
        }}
      />
    </>
  );
}

function EmptyState({ onPick }: { onPick: (q: string) => void }): ReactElement {
  return (
    <div className="mx-auto flex h-full w-full max-w-4xl flex-col items-center justify-center px-6 text-center">
      <div className="relative">
        <div className="absolute inset-0 rounded-2xl bg-primary-500/20 blur-2xl" />
        <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-pink shadow-2xl shadow-primary-500/40">
          <Wand2 className="h-7 w-7 text-white" />
        </div>
      </div>
      <h2 className="mt-5 max-w-lg px-2 text-2xl font-semibold leading-tight text-white sm:text-3xl">
        Ask about your air. Get answers, instantly.
      </h2>
      <p className="mt-3 max-w-lg text-base text-gray-400">
        I read your sensor data, surface what matters, and draw the chart you would have asked for
        next.
      </p>
      <div className="mt-8 grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
        {SUGGESTIONS.map((s) => (
          <button
            key={s.label}
            type="button"
            onClick={() => onPick(s.prompt)}
            className="group flex items-center gap-3 rounded-xl border border-gray-800 bg-gray-900/60 px-4 py-3.5 text-left text-base text-gray-100 transition hover:-translate-y-0.5 hover:border-primary-500/50 hover:bg-gray-900"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary-500/20 to-pink/20 text-primary-300 group-hover:from-primary-500/30 group-hover:to-pink/30">
              {s.icon}
            </span>
            <span className="flex-1">{s.label}</span>
            <Zap className="h-4 w-4 text-gray-600 transition group-hover:text-primary-400" />
          </button>
        ))}
      </div>
    </div>
  );
}

function PhaseIndicator({ phase }: { phase: string }): ReactElement {
  return (
    <div className="mb-3 flex items-center gap-2 rounded-lg bg-primary-500/10 px-3 py-2.5 text-sm text-primary-200">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span className="font-medium">{phase}</span>
    </div>
  );
}

function AssistantBody({
  message,
  displayMap,
  phase
}: {
  message: UIMessage;
  displayMap: DisplayMap;
  phase: string | null;
}): ReactElement {
  const parts = message.parts || [];
  const text = messageText(message);

  return (
    <div>
      {phase && <PhaseIndicator phase={phase} />}

      {parts.map((part, i) => {
        if (part.type === 'text') {
          const value = (part as { text?: string }).text;
          if (!value) return null;
          return (
            <div key={i} className="prose prose-invert max-w-none text-base leading-relaxed">
              <MarkdownText>{value}</MarkdownText>
            </div>
          );
        }

        if (isToolUIPart(part)) {
          if (part.state === 'output-error') {
            return (
              <div
                key={i}
                className="my-2 rounded-lg border border-error-500/30 bg-error-500/10 px-3 py-2 text-sm text-error-300"
              >
                {getToolName(part)} failed: {part.errorText || 'unknown error'}
              </div>
            );
          }
          if (part.state === 'output-available') {
            const blocks = displayMap[part.toolCallId];
            if (!blocks || !blocks.length) return null;
            return (
              <Fragment key={i}>
                {blocks.map((b, j) => (
                  <AgentBlock key={j} block={b} />
                ))}
              </Fragment>
            );
          }
          return null;
        }

        return null;
      })}

      {!phase && !text && !parts.some((p) => isToolUIPart(p)) && (
        <div className="text-base text-gray-400">No insight produced.</div>
      )}
    </div>
  );
}

function MessageView({
  message,
  displayMap,
  phase
}: {
  message: UIMessage;
  displayMap: DisplayMap;
  phase: string | null;
}): ReactElement {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        // Assistant messages take the full width so charts can use all the
        // available space; user messages stay capped so the text bubble
        // doesn't span the whole row.
        className={`rounded-2xl px-4 py-3 ${
          isUser
            ? 'max-w-[80%] bg-gradient-to-br from-primary-600 to-pink text-white shadow-lg shadow-primary-900/40'
            : 'w-full border border-gray-800/80 bg-gray-900/70 text-gray-100 backdrop-blur'
        }`}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap text-base leading-relaxed">
            {messageText(message)}
          </div>
        ) : (
          <AssistantBody message={message} displayMap={displayMap} phase={phase} />
        )}
      </div>
    </div>
  );
}

function composerPlaceholder(disabled: boolean, upload: UploadedDataset | null): string {
  if (disabled) return 'Working on your last question…';
  if (upload) return `Ask about ${upload.filename}, or anything about your sensor data…`;
  return 'Ask anything about your sensor data — e.g. “show CO₂ over the last 24h”';
}

function Composer({
  input,
  setInput,
  disabled,
  onSubmit,
  uploadedData,
  uploadError,
  onUploadFile,
  onClearUpload
}: {
  input: string;
  setInput: (s: string) => void;
  disabled: boolean;
  onSubmit: () => void;
  uploadedData: UploadedDataset | null;
  uploadError: string | null;
  onUploadFile: (file: File) => void;
  onClearUpload: () => void;
}): ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="relative z-10 border-t border-gray-800/70 bg-gray-900/80 px-4 py-3 backdrop-blur">
      <div className="mx-auto max-w-4xl px-6">
        {(uploadedData || uploadError) && (
          <div className="mb-2">
            {uploadedData && (
              <div className="flex items-center gap-2 rounded-lg border border-primary-500/40 bg-primary-500/10 px-3 py-2 text-sm">
                <FileSpreadsheet className="h-4 w-4 shrink-0 text-primary-300" />
                <span className="min-w-0 flex-1 truncate text-primary-100">
                  <span className="font-medium">{uploadedData.filename}</span>
                  <span className="ml-2 text-primary-300/80">
                    {uploadedData.rows.length} rows · {uploadedData.headers.length} cols
                  </span>
                </span>
                <button
                  type="button"
                  onClick={onClearUpload}
                  title="Remove attachment"
                  className="rounded-md p-1 text-primary-300 hover:bg-primary-500/20 hover:text-white"
                >
                  <CloseIcon className="h-4 w-4" />
                </button>
              </div>
            )}
            {uploadError && (
              <div className="rounded-lg border border-error-500/40 bg-error-500/10 px-3 py-2 text-sm text-error-200">
                {uploadError}
              </div>
            )}
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="relative"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                onSubmit();
              } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmit();
              }
            }}
            rows={3}
            placeholder={composerPlaceholder(disabled, uploadedData)}
            disabled={disabled}
            className="w-full resize-none rounded-2xl border border-gray-700/70 bg-gray-900/80 py-4 pl-14 pr-16 text-base text-white placeholder:text-gray-500 shadow-inner focus:border-primary-500/60 focus:outline-none focus:ring-2 focus:ring-primary-500/40 disabled:opacity-60"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.tsv,.txt,.xls,.xlsx"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUploadFile(f);
              // Allow re-uploading the same file.
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            title="Attach a CSV or Excel file"
            className="absolute bottom-4 left-4 flex h-10 w-10 items-center justify-center rounded-xl border border-gray-700/70 bg-gray-900 text-gray-400 transition hover:border-primary-500/60 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          <button
            type="submit"
            disabled={disabled || !input.trim()}
            className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-pink text-white shadow-lg shadow-primary-500/40 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {disabled ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        </form>
        <div className="mt-2 text-center text-xs text-gray-600">
          Enter to send · Shift+Enter for newline · Paperclip to attach CSV/Excel
        </div>
        <div className="mt-1 text-center text-xs text-gray-500">
          Insight Agent is in beta and can make mistakes. Verify important results.
        </div>
      </div>
    </div>
  );
}

// Bar of guarded chart-appearance controls. Lives directly below the header
// when toggled on. Each control is bounded (slider ranges, color inputs) so
// the user cannot drive the charts into a broken state.
function ChartAppearanceBar({
  settings,
  onChange,
  onReset,
  onClose
}: {
  settings: ChartSettings;
  onChange: (partial: Partial<ChartSettings>) => void;
  onReset: () => void;
  onClose: () => void;
}): ReactElement {
  return (
    <div className="relative z-10 border-b border-gray-800/70 bg-gray-900/80 px-5 py-3 text-sm text-gray-200 backdrop-blur">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-4">
        <span className="text-xs uppercase tracking-wide text-gray-500">Chart appearance</span>
        <label htmlFor="cs-fontsize" className="flex items-center gap-2">
          <span className="text-gray-400">Font</span>
          <input
            id="cs-fontsize"
            type="range"
            min={10}
            max={28}
            step={1}
            value={settings.fontSize}
            onChange={(e) => onChange({ fontSize: Number(e.target.value) })}
            className="h-1.5 w-32 cursor-pointer accent-primary-500"
          />
          <span className="w-10 text-right tabular-nums text-gray-300">{settings.fontSize}px</span>
        </label>
        <label htmlFor="cs-tickcolor" className="flex items-center gap-2">
          <span className="text-gray-400">Ticks</span>
          <input
            id="cs-tickcolor"
            type="color"
            value={settings.tickColor}
            onChange={(e) => onChange({ tickColor: e.target.value })}
            className="h-7 w-9 cursor-pointer rounded border border-gray-700 bg-transparent"
            title="Axis tick / legend / SVG text color"
          />
        </label>
        <label htmlFor="cs-gridcolor" className="flex items-center gap-2">
          <span className="text-gray-400">Grid</span>
          <input
            id="cs-gridcolor"
            type="color"
            value={settings.gridColor}
            onChange={(e) => onChange({ gridColor: e.target.value })}
            className="h-7 w-9 cursor-pointer rounded border border-gray-700 bg-transparent"
            title="Grid line color"
          />
        </label>
        <label htmlFor="cs-seriescolor" className="flex items-center gap-2">
          <span className="text-gray-400">Series</span>
          <input
            id="cs-seriescolor"
            type="color"
            value={settings.seriesColor || '#6366F1'}
            onChange={(e) => onChange({ seriesColor: e.target.value })}
            className="h-7 w-9 cursor-pointer rounded border border-gray-700 bg-transparent"
            title="Override colour for ALL data lines / bars / scatter points / box strokes. Click 'Clear' to restore each parameter's palette colour."
          />
          {settings.seriesColor ? (
            <button
              type="button"
              onClick={() => onChange({ seriesColor: '' })}
              className="rounded border border-gray-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-400 hover:border-primary-500/60 hover:text-white"
              title="Restore each parameter's palette colour"
            >
              Clear
            </button>
          ) : (
            <span className="text-[10px] uppercase tracking-wide text-gray-600">palette</span>
          )}
        </label>
        <label htmlFor="cs-showgrid" className="flex cursor-pointer items-center gap-1.5">
          <input
            id="cs-showgrid"
            type="checkbox"
            checked={settings.showGrid}
            onChange={(e) => onChange({ showGrid: e.target.checked })}
            className="h-4 w-4 cursor-pointer accent-primary-500"
          />
          <span className="text-gray-300">Show grid</span>
        </label>
        <label htmlFor="cs-showlegend" className="flex cursor-pointer items-center gap-1.5">
          <input
            id="cs-showlegend"
            type="checkbox"
            checked={settings.showLegend}
            onChange={(e) => onChange({ showLegend: e.target.checked })}
            className="h-4 w-4 cursor-pointer accent-primary-500"
          />
          <span className="text-gray-300">Show legend</span>
        </label>
        <button
          type="button"
          onClick={onReset}
          className="ml-auto rounded-md border border-gray-700 px-3 py-1 text-xs text-gray-300 transition hover:border-primary-500/60 hover:text-white"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-gray-500 transition hover:bg-gray-800 hover:text-gray-200"
          title="Close"
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="mx-auto mt-1 max-w-5xl text-xs text-gray-500">
        Applies to every chart on this page. Inputs are clamped — settings cannot break the charts.
      </div>
    </div>
  );
}

const TelosAgentPage = (): ReactElement => {
  const { devices, dataSummary, deviceDataSummary, activePaypalSubscription } = useData();
  const { maxDataDays } = usePlan();
  // Only advertise a history limit when a subscription actually imposes one:
  // with no active plan the server does not restrict the range (see
  // PlanDateRangeValidator), so claiming a cap here would be wrong.
  const historyDays = activePaypalSubscription ? maxDataDays : null;

  const [input, setInput] = useState('');
  // Render blocks (charts / KPI cards) are produced by client-side tool
  // executors and kept here, keyed by toolCallId, so the model context stays
  // lean (it only ever sees the compact `data` payload).
  const [displayMap, setDisplayMap] = useState<DisplayMap>({});
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Validated chart appearance, declared early so the agent tools can be
  // given a stable updater. All updates flow through clampChartSettings so
  // a bad value can never reach the chart layer.
  const [showSettings, setShowSettings] = useState(false);
  const [chartSettings, setChartSettings] = useState<ChartSettings>(DEFAULT_CHART_SETTINGS);
  const updateSettings = useCallback((partial: Partial<ChartSettings>): void => {
    setChartSettings((s) => clampChartSettings({ ...s, ...partial }));
  }, []);

  // Uploaded dataset (CSV / Excel) — parsed in the browser, exposed to the
  // agent only via the `read_uploaded_dataset` tool.
  const [uploadedData, setUploadedData] = useState<UploadedDataset | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const handleUploadFile = useCallback(async (file: File): Promise<void> => {
    setUploadError(null);
    try {
      const parsed = await parseUploadedFile(file);
      if (!parsed) {
        setUploadError(
          `Could not parse "${file.name}" — supported formats are .csv / .tsv / .xlsx / .xls.`
        );
        return;
      }
      setUploadedData(parsed);
    } catch (err) {
      setUploadError(`Failed to parse "${file.name}": ${(err as Error)?.message || err}`);
    }
  }, []);
  const handleClearUpload = useCallback((): void => {
    setUploadedData(null);
    setUploadError(null);
  }, []);

  // A dataset handed over from the OPC bin page: pre-load it and pre-fill the
  // question, but never send it — the user still presses Send.
  useEffect(() => {
    const handoff = takeChartAiHandoff();
    if (!handoff) return;
    setUploadedData(handoff.dataset);
    setInput(handoff.prompt);
  }, []);

  // Tool executors depend on the live data context; keep them in a ref so the
  // (stable) onToolCall handler always runs the freshest version.
  const agentExecutors = useMemo(
    () =>
      buildAgentTools({
        dataSummary: dataSummary as any,
        deviceDataSummary: deviceDataSummary as any,
        devices,
        updateChartSettings: updateSettings,
        uploadedData,
        historyDays
      }),
    [devices, dataSummary, deviceDataSummary, updateSettings, uploadedData, historyDays]
  );
  const executorsRef = useRef(agentExecutors);
  useEffect(() => {
    executorsRef.current = agentExecutors;
  }, [agentExecutors]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: getAiChatUrl(),
        // Sent on every request so the Spring Boot proxy authenticates the
        // user before forwarding to the Node AI server.
        headers: () => {
          const token = getTokenFromSessionStorage();
          const userName = getUserNameFromSessionStorage();
          const headers: Record<string, string> = {};
          if (token) headers.Authorization = `Bearer ${token}`;
          if (userName) headers.user_name = String(userName);
          return headers;
        },
        // The agent needs the browser's time zone to date its prompt, so
        // "plot CO2 from July 7 to July 14" resolves against the user's
        // calendar. "Now" comes from the server's clock, not this one.
        body: () => ({
          clientContext: {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        })
      }),
    []
  );

  const { messages, sendMessage, setMessages, status, error, addToolOutput } =
    useChat({
      transport,
      // Once the model's tool calls all have results, resume the loop.
      sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
      onToolCall: ({ toolCall }) => {
        // Dynamic tools are not used here; ignore defensively.
        if ((toolCall as { dynamic?: boolean }).dynamic) return;
        const { toolName, toolCallId } = toolCall;
        const args = (toolCall.input as Record<string, unknown>) || {};
        const exec = executorsRef.current[toolName];

        if (!exec) {
          addToolOutput({
            tool: toolName,
            toolCallId,
            output: { data: { error: `Unknown tool: ${toolName}` } }
          });
          return;
        }

        // Run async; call addToolOutput when done (do not await it — that can
        // deadlock the auto-resume).
        exec(args)
          .then((result) => {
            if (result.display && result.display.length) {
              setDisplayMap((m) => ({ ...m, [toolCallId]: result.display as RenderBlock[] }));
            }
            addToolOutput({
              tool: toolName,
              toolCallId,
              output: result.note
                ? { note: result.note, data: result.data }
                : { data: result.data }
            });
          })
          .catch((err: unknown) => {
            console.error(`[agent tool ${toolName}]`, err);
            addToolOutput({
              tool: toolName,
              toolCallId,
              output: {
                data: { error: String((err as Error)?.message || err) }
              }
            });
          });
      }
    });

  const isLoading = isAgentWorking(messages, status);
  const phase = derivePhase(messages, status);

  useEffect(() => {
    scrollerRef.current?.scrollTo({
      top: scrollerRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages]);

  // Saved-chat persistence — see services/chat-storage.ts. We track the
  // current chat by id (mirrored to ?chatId= so a refresh keeps the user in
  // the same chat), persist its messages + display blocks whenever a turn
  // ends, and let the user pick previous chats from a side drawer.
  const [searchParams, setSearchParams] = useSearchParams();
  const [chatId, setChatId] = useState<string>(
    () => searchParams.get('chatId') || newChatId()
  );
  const [savedChats, setSavedChats] = useState<ChatSummary[]>([]);
  const [showChats, setShowChats] = useState(false);

  // Load the user's saved chats (server-backed) once on mount.
  useEffect(() => {
    listChats().then(setSavedChats);
  }, []);

  // Mount: if the URL chatId points to a saved chat, restore its messages +
  // chart blocks. If the id has no saved chat (deleted / different browser),
  // we still keep that id so the URL stays consistent — the chat just starts
  // empty and will be saved under that id once the user sends a message.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const fromUrl = searchParams.get('chatId');
    if (!fromUrl) return;
    (async (): Promise<void> => {
      const saved = await loadChat(fromUrl);
      if (saved && saved.messages.length > 0) {
        setMessages(saved.messages as any);
        setDisplayMap(saved.displayMap || {});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror chatId → URL so a refresh (or shared link) lands on the same chat.
  useEffect(() => {
    if (searchParams.get('chatId') === chatId) return;
    const next = new URLSearchParams(searchParams);
    next.set('chatId', chatId);
    setSearchParams(next, { replace: true });
  }, [chatId, searchParams, setSearchParams]);

  // Persist when a turn ends (status leaves streaming/submitted). Saving on
  // every token would thrash the API during streaming. The server upserts by
  // id and preserves the original createdAt, and deriveChatTitle keys off the
  // (immutable) first user message, so we needn't fetch the existing chat.
  useEffect(() => {
    if (status === 'streaming' || status === 'submitted' || messages.length === 0) {
      return undefined;
    }
    let cancelled = false;
    (async (): Promise<void> => {
      await saveChat({
        id: chatId,
        title: deriveChatTitle(messages),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        messages,
        displayMap
      });
      if (cancelled) return;
      setSavedChats(await listChats());
    })();
    return () => {
      cancelled = true;
    };
  }, [messages, displayMap, chatId, status]);

  const handleNewChat = useCallback((): void => {
    if (isLoading) return;
    setChatId(newChatId());
    setMessages([]);
    setDisplayMap({});
    setShowChats(false);
  }, [isLoading, setMessages]);

  const handleSelectChat = useCallback(
    (id: string): void => {
      if (isLoading) return;
      if (id === chatId) {
        setShowChats(false);
        return;
      }
      (async (): Promise<void> => {
        const saved = await loadChat(id);
        if (!saved) return;
        setChatId(id);
        setMessages(saved.messages as any);
        setDisplayMap(saved.displayMap || {});
        setShowChats(false);
      })();
    },
    [isLoading, chatId, setMessages]
  );

  const handleDeleteChat = useCallback(
    (id: string): void => {
      deleteChat(id).then(listChats).then(setSavedChats);
      if (id === chatId) handleNewChat();
    },
    [chatId, handleNewChat]
  );

  const submit = (text: string): void => {
    if (!text.trim() || isLoading) return;
    sendMessage({ text: text.trim() });
    setInput('');
  };

  const reset = (): void => {
    if (isLoading) return;
    setMessages([]);
    setDisplayMap({});
    // Reset starts a fresh chat id so the previous saved chat stays intact.
    setChatId(newChatId());
  };

  return (
    <ChartSettingsProvider value={chartSettings}>
      <div className="relative -mx-2 lg:-mx-6 flex h-[calc(100vh-var(--header-height,4rem))] w-[calc(100%+1rem)] lg:w-[calc(100%+3rem)] max-w-none flex-col overflow-hidden bg-gradient-to-b from-gray-900 via-gray-900 to-gray-900">
        <BackgroundOrnament />

        <header className="relative z-10 flex items-center justify-between border-b border-gray-800/70 bg-gray-900/60 px-5 py-3.5 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 rounded-lg bg-primary-500/30 blur-xl" />
              <div className="relative flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-pink shadow-lg shadow-primary-500/40">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-white">Insight Agent</h1>
                <span className="rounded-full border border-primary-500/40 bg-primary-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-300">
                  Beta
                </span>
              </div>
              <p className="text-sm text-gray-400">
                Ask anything about your sensors — answers, trends, and charts.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowChats(true)}
              title="Chat history"
              className="relative rounded-md p-2 text-gray-400 transition hover:bg-gray-800 hover:text-white"
            >
              <MessagesSquare className="h-5 w-5" />
              {savedChats.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-semibold text-white">
                  {savedChats.length > 99 ? '99+' : savedChats.length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowSettings((v) => !v)}
              title="Chart appearance"
              className={`rounded-md p-2 transition hover:bg-gray-800 ${
                showSettings ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Settings2 className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={reset}
              disabled={isLoading}
              title="New chat (the current one stays in history)"
              className="flex items-center gap-1.5 rounded-md border border-primary-500/40 bg-primary-500/10 px-3 py-1.5 text-sm font-medium text-primary-100 transition hover:border-primary-500/60 hover:bg-primary-500/15 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">New chat</span>
            </button>
          </div>
        </header>

        <ChartSidebar
          open={showChats}
          chats={savedChats}
          currentChatId={chatId}
          onSelect={handleSelectChat}
          onNew={handleNewChat}
          onDelete={handleDeleteChat}
          onClose={() => setShowChats(false)}
        />

        {showSettings && (
          <ChartAppearanceBar
            settings={chartSettings}
            onChange={updateSettings}
            onReset={() => setChartSettings(DEFAULT_CHART_SETTINGS)}
            onClose={() => setShowSettings(false)}
          />
        )}

        <div ref={scrollerRef} className="relative z-10 flex-1 overflow-y-auto">
          {messages.length === 0 ? (
            <EmptyState onPick={submit} />
          ) : (
            <div className="flex w-full flex-col gap-4 px-6 py-6">
              {messages.map((m, idx) => (
                <MessageView
                  key={m.id}
                  message={m}
                  displayMap={displayMap}
                  phase={idx === messages.length - 1 ? phase : null}
                />
              ))}
              {error && (
                <div className="rounded-xl border border-error-500/30 bg-error-500/10 px-4 py-3 text-base text-error-200">
                  <strong>The agent failed.</strong> {error.message}
                  <div className="mt-1 text-sm text-error-300/80">
                    Check that the AI server is reachable and try again.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <Composer
          input={input}
          setInput={setInput}
          disabled={isLoading}
          onSubmit={() => submit(input)}
          uploadedData={uploadedData}
          uploadError={uploadError}
          onUploadFile={handleUploadFile}
          onClearUpload={handleClearUpload}
        />
      </div>
    </ChartSettingsProvider>
  );
};

export default TelosAgentPage;
